import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Shield, ChevronDown } from "lucide-react";
import { useCookieConsent } from "./CookieConsentContext";

export default function CookieBanner() {
  const { bannerVisible, acceptAll, acceptNecessary, saveCustom } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [externalMedia, setExternalMedia] = useState(false);

  if (!bannerVisible) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed bottom-0 left-0 right-0 p-6"
        style={{ zIndex: 99999 }}
        data-testid="cookie-banner"
      >
        <div className="max-w-3xl mx-auto border border-gold/15 rounded-2xl p-8 cookie-banner-glass" style={{ boxShadow: "0 -4px 40px rgba(0,0,0,0.7)" }}>
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h3 className="font-unbounded text-base font-bold text-cream mb-1">
                Cookie-Einstellungen
              </h3>
              <p className="font-inter text-xs text-cream/50 leading-relaxed">
                Wir verwenden Cookies und externe Dienste, um dir das beste Erlebnis zu bieten.
                Du kannst selbst entscheiden, welche Kategorien du zulassen möchtest.
              </p>
            </div>
          </div>

          {/* Details Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 font-space-grotesk text-xs text-gold/70 hover:text-gold transition-colors mb-4"
            data-testid="cookie-details-toggle"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDetails ? "rotate-180" : ""}`} />
            Details anzeigen
          </button>

          {/* Category Details */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden mb-4"
              >
                <div className="space-y-3">
                  {/* Necessary */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5">
                    <div>
                      <span className="font-space-grotesk text-xs font-semibold text-cream">Notwendig</span>
                      <p className="font-inter text-xs text-cream/40 mt-0.5">Grundlegende Funktionen der Website.</p>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-gold/15 text-gold font-space-grotesk text-xs font-semibold">
                      Immer aktiv
                    </div>
                  </div>

                  {/* External Media */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5">
                    <div>
                      <span className="font-space-grotesk text-xs font-semibold text-cream">Externe Medien</span>
                      <p className="font-inter text-xs text-cream/40 mt-0.5">Google Maps, Matterport und andere eingebettete Inhalte.</p>
                    </div>
                    <button
                      onClick={() => setExternalMedia(!externalMedia)}
                      className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${
                        externalMedia ? "bg-gold" : "bg-white/10"
                      }`}
                      data-testid="cookie-toggle-external"
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${
                          externalMedia ? "translate-x-[22px]" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Buttons */}
          <div className="flex flex-row gap-3">
            <button
              onClick={acceptAll}
              className="flex-1 btn-gold py-3 rounded-xl text-xs"
              data-testid="cookie-accept-all"
            >
              Alle akzeptieren
            </button>
            {showDetails ? (
              <button
                onClick={() => saveCustom({ externalMedia })}
                className="flex-1 btn-outline py-3 rounded-xl text-xs"
                data-testid="cookie-save-custom"
              >
                Auswahl speichern
              </button>
            ) : (
              <button
                onClick={acceptNecessary}
                className="flex-1 btn-outline py-3 rounded-xl text-xs"
                data-testid="cookie-accept-necessary"
              >
                Nur notwendige
              </button>
            )}
          </div>
        </div>
      </div>
    </AnimatePresence>
  );
}
